// The model-registry read surface, typed — `GET /api/models/{modelId}`.
//
// WHY A MODULE OF ITS OWN rather than a method on `HttpBennieClient`. That client is the
// WORKSPACE contract: every path it owns hangs off `/workspaces/{id}`, and its constructor
// threads workspace-scoped concerns (export timeouts, chat recovery baselines) that a registry
// read has nothing to say about. `views/reggie/reggieWsApi.ts` is the precedent for a
// second top-level product surface owning its own thin client, and it is the shape copied here:
// one place knows the wire, hooks and views consume typed results.
//
// ⚠️ COPIED THE MECHANISM, NOT THE CONTRACT. The reggie client's error vocabulary and its
// workspace-shaped types do not carry over; only the fetch discipline does — a bounded timeout on
// every call, a typed error carrying a closed-vocabulary code, and a body that is never leaked to
// the UI. Per core-principles: reuse the mechanism, adapt the contract.
//
// ⚠️ AMENDED 2026-08-23 (B-3) — THE TRANSPORT ITSELF NOW LIVES IN `modelRegistryFetch.ts`. This
// module had grown a private `modelFetch` + `jsonOrThrow` + `ModelApiError`, and `mdrApi.ts` had
// grown a structurally identical set against the SAME base and the SAME error envelope. They are
// one set now. What stayed here is what is genuinely this module's: the model-record wire TYPES and
// the routes that read and write them.
//
// ⛔ `ModelApiError` IS NOT RE-EXPORTED FROM HERE, DELIBERATELY. A re-export would leave two import
// paths for one symbol and a grep for the real one would under-count — the same two-copies-drift
// this consolidation exists to end. Consumers import it from `./modelRegistryFetch`.

import { DEFAULT_TIMEOUT_MS, jsonOrThrow, modelFetch, postJson } from './modelRegistryFetch';
import { REGISTRATION_FACT_KEYS } from './modelFactsApi';
import type { PrincipalMap } from './principalDisplay';

/** The `models` row as `ModelRegistryRow` serialises it (snake_case, unaltered). */
export interface ModelRow {
  model_id: string;
  name: string;
  model_type: string;
  status: string;
  scope_path: string;
  product_category?: string | null;
  applied_to?: string[] | null;
  applied_pairs?: string[] | null;
  gd_available: boolean;
  created_by: string;
  archived_at?: string | null;
  archived_by?: string | null;
  row_version: number;
  created_at: string;
  updated_at: string;
}

/**
 * WI-10a's per-caller block. `role` is what the caller IS; `may_write` is the VERDICT.
 *
 * ⚠️ THE TWO ARE NOT INTERCHANGEABLE AND THE SPLIT IS DELIBERATE — an owner of an ARCHIVED model
 * has `role: 'owner'` and `may_write: false`. Rendering an affordance off `role` would offer a
 * button the API refuses. Branch on `may_write`; use `role` for display only.
 */
export interface ModelCallerAccess {
  role: string | null;
  may_write: boolean;
  write_denied_reason: string | null;
  /**
   * ➕ 2026-10-26 — the Test Ground WRITE verdict (the Access tab's "Collaborator" grant, the `reggie`
   * slug), published beside `may_write` because the two answer different questions. Optional: a new
   * client against an older API is a real state, and absent reads as NOT granted (fail closed).
   */
  may_write_test_ground?: boolean;
  /** Closed vocabulary of `write_denied_reason`; resolve with `resolveModelWriteDenied`. */
  test_ground_write_denied_reason?: string | null;
  /**
   * ➕ WI-91 — the SAME verdict, per SERVICE TYPE, for work created BENEATH this model.
   *
   * ⚠️ `may_write` above is the MODEL-ROW verdict and answers *"may I edit the model record"*. It is
   * `false` for EVERY collaborator by design, while `POST /api/workspaces` admits a collaborator
   * ticked for a service type with a 201. Gating a CREATE control on `may_write` therefore greys a
   * control the server accepts — the FE being stricter than the API, which is the worse direction.
   * Use `createVerdictFor` (`./ModelProjects`), never this field, for a create affordance.
   *
   * ⚠️ **ABSENCE IS NOT DENIAL.** The map is absent wholesale on any payload built before this
   * field, and an individual key is absent for a service type that is neither a defence line nor
   * covered by the caller's entitlement. `createVerdictFor` encodes the one correct reading.
   *
   * 🔴 **CORRECTED 2026-09-05 (`W-03`).** This read *"A key is present only for a service type the
   * caller's deployment-wide entitlement covers"*, and that was the defect: a caller entitled to
   * nothing received `{"toolbox": …}` — not empty, but carrying no defence line — so every question
   * the Projects tab asks fell through to the model-row verdict, and WI-91 was a no-op for exactly
   * the callers it was written for. The API now publishes the three defence lines unconditionally.
   */
  service_types?: Record<string, ModelCallerServiceTypeAccess>;
}

/** ➕ WI-91 — one entry of {@link ModelCallerAccess.service_types}. No `role`: it is a property of
 *  the caller's row, identical across service types, and lives once on the parent block. */
export interface ModelCallerServiceTypeAccess {
  may_write: boolean;
  write_denied_reason: string | null;
  /**
   * ➕ **W-04 / ruling `R-B1` (2026-09-05) — the CREATE-TIME verdict, published as its own value.**
   *
   * ⚠️ **IT IS NOT `may_write`, AND THE GAP IS NOT A ROUNDING ERROR.** `POST /api/workspaces` runs
   * the deployment-entitlement gate FIRST — `permitted.Contains(type)` — and refuses with a 400
   * before the per-model leg runs. The write resolver, by ruling `R-D`, short-circuits on `owner`
   * BEFORE that same axis. The two gates therefore answer different questions, which is why there
   * are two fields.
   *
   * 🔴 **STRUCK AND CORRECTED 2026-09-08 (operator ruling `R-C1`).** This read
   * ~~"`permitted.Contains(type)` for every non-admin, **with no owner arm** … So a **non-admin
   * OWNER** on a type they are not entitled to publishes `may_write: true` while the door answers
   * 400"~~. The door NOW HAS AN OWNER ARM: a model's owner may start work of any licensed service
   * type under their own model, so for that tier the two verdicts AGREE and `may_create` is `true`.
   * The tiers this field still discriminates are the ones where they genuinely differ — a ticked
   * collaborator (`may_write` false on the MODEL ROW, creatable beneath it), an unentitled
   * collaborator or stranger, and an archived model.
   *
   * ⚠️ **AND THE HAZARD IS NOW SYMMETRIC.** Reading `may_write` here was LOOSER than the server;
   * after `R-C1`, a producer that forgot the owner arm would be STRICTER than it — a greyed control
   * over an operation the API accepts. Both are the same lie about the server.
   *
   * ⚠️ **OPTIONAL, AND THAT IS A DEPLOY FACT RATHER THAN A CONVENIENCE.** `apps/web` (SWA) and
   * `apps/api` (container app) ship as separate artifacts, so a new client against an older API is a
   * real state. Absent here means *"this entry carries no create verdict"* — never *"denied"*.
   */
  may_create?: boolean;
  /** The reason behind a false {@link may_create}, from the SAME closed vocabulary
   *  `write_denied_reason` uses. Absent/​null exactly when `may_create` is true. */
  create_denied_reason?: string | null;
}

export interface ModelDetail {
  model: ModelRow;
  caller: ModelCallerAccess;
  /**
   * ➕ WI-57 — display names for the oids THIS payload mentions: `created_by` and, when present,
   * `archived_by`. Read it through `principalDisplay.ts`, never by indexing directly — the map has
   * four states and only one of them is a name.
   *
   * ⚠️ GATED ON `caller.may_write`, which on this surface is the same verdict as `may_manage_access`
   * (`R-Q10` set both to `isAdmin || isOwner`). A caller who may not manage the model gets an entry
   * per oid stamped `not_disclosed` rather than a missing key — "you are not shown this" and "the
   * directory could not be reached" are different facts and the UI must be able to tell them apart.
   */
  principals?: PrincipalMap;
}

/**
 * The LIST route's shape. ⚠️ NOTE WHAT IS ABSENT: no `caller` block.
 *
 * Measured on the wire during the 2026-08-22 walk — `GET /api/models` returns `{ models, count }`
 * and nothing per-caller, while `GET /api/models/{id}` carries the `caller` verdict. That asymmetry
 * is deliberate on the API side and load-bearing here: **a per-row write affordance would need N+1
 * point reads.** It does not need one — locked decision L1 makes reads universal and R8 is
 * "role greys, it never hides" at PAGE level, so the list NAVIGATES and the homepage owns the
 * verdict. ⛔ Never derive `may_write` client-side from `status` plus an identity: that is a second
 * opinion about authorization, and a second opinion drifts from the gate.
 */
export interface ModelListResponse {
  models: ModelRow[];
  count: number;
  /**
   * ➕ WI-57 — display names for the REGISTRARS (`created_by`) of the models in this page.
   *
   * 🔴 GATED MORE NARROWLY THAN THE POINT READ, AND THE NARROWING IS THE SERVER'S, NOT A BUG HERE.
   * The list route discloses names only to an ADMIN, because reaching the per-model
   * `may_manage_access` verdict for every row needs a store op that does not exist (see the route's
   * own comment and `TD-939`). `isAdmin` implies that verdict on every active model, so this is a
   * strict subset of what is permitted — never a widening. The consequence a reader will notice:
   * a NON-ADMIN OWNER sees a name on their model's Access tab and the oid on the dashboard.
   * ⛔ Do NOT "fix" that here by re-deriving the verdict client-side; that is a second opinion about
   * authorization, which the note above already refuses for `may_write`.
   */
  principals?: PrincipalMap;
}

/**
 * Every model the caller may see — which, per L1, is every model.
 *
 * ⛔ NO `status` FILTER IS SENT, deliberately. The route accepts one and refuses anything but
 * `active`/`archived`; filtering to `active` here would make an archived model VANISH rather than
 * appear as archived, which is the same shape `R-C` rules against one level up for projects.
 */
export async function listModels(): Promise<ModelListResponse> {
  return jsonOrThrow<ModelListResponse>(await modelFetch(''));
}

/**
 * Read one model plus the caller's access verdict.
 *
 * ⚠️ THIS IS THE POINT READ. The LIST route (`GET /api/models`) omits `caller` entirely — measured
 * on the wire during the 2026-08-22 walk — so a per-row write affordance on a list needs N+1 point
 * reads. Recorded here because the alternative (deriving `may_write` client-side from `status` and
 * an identity) is a SECOND OPINION about authorization, and a second opinion drifts.
 */
export async function getModel(modelId: string): Promise<ModelDetail> {
  return jsonOrThrow<ModelDetail>(await modelFetch(`/${encodeURIComponent(modelId)}`));
}

/**
 * `POST /api/models` body — a MIRROR of `Bennie.Api.Models.RegisterModelRequest`, and deliberately
 * only of what that type actually declares.
 *
 * ⚠️ <s>THREE FIELDS THE REGISTRATION *DESIGN* ASKS FOR ARE ABSENT HERE BECAUSE THEY ARE ABSENT ON
 * THE WIRE</s>, and naming them in this type would be the more expensive mistake — a caller would
 * populate them, `System.Text.Json` would drop them silently, and the loss would surface as a blank
 * field on the model homepage rather than here as a compile error:
 *  - <s>**`description`** (locked rule R49, "the description is a canonical, not a form field") — no
 *    column on `models`, no property on `RegisterModelRequest`.</s>
 *  - **the MAPPED ID** (R32 / D7) — no column, no property. D7's own consequence is that its
 *    absence is NORMAL, so nothing here should pretend to carry it.
 *  - <s>**the canonical block** (`model_facts`) — the TABLE exists in `0044_model_registry.sql`, but
 *    there is no route, no repository op and no request/response type anywhere in `apps/api`
 *    (grepped 2026-08-22: the only `apps/api` matches for `model_facts` are three comments).</s>
 *
 * ➕ **CORRECTED 2026-09-03 (WI-62). TWO OF THE THREE WERE FALSE AT HEAD, AND ONE OF THEM HAD BEEN
 * FALSE FOR A WEEK.** `RegisterModelRequest.Description` shipped with WI-20 on 2026-08-25 and this
 * mirror went on omitting it — which is the WHITELIST-DROP this correction is about, not a footnote
 * to it. An omitted optional property is not a type error, so a value typed into a form would have
 * been dropped by the serializer with nothing on either side to notice. The canonical block likewise
 * has a route (`GET/PUT /api/models/{id}/facts`), a repository op and a request type. **`mapped_id`
 * is the one clause that is still exactly true**, and it is kept rather than restated so a reader
 * can see which third survived. The struck text stays because the REASONING was correct and is the
 * reason this type is now exhaustive; only its premises expired.
 *
 * ⛔ **`model_owner` IS ABSENT AND THAT IS A DECISION** — ⚖️ and as of B14 `W-06` (2026-09-05) it
 * is absent from the CANONICAL SET ENTIRELY, not merely from this door. The owner is DERIVED from
 * `model_access` and drawn as a non-input row. This used to point at
 * <s>`NOT_DECLARED_AT_REGISTRATION`</s>, the one-entry pre-cut list in `modelFactsApi.ts`; that list
 * was deleted with the key it held.
 *
 * ⚠️ AND `model_type` IS ABSENT ON PURPOSE even though the property exists. 0044's stated behaviour
 * is *"the API defaults it from the scope leaf at registration"* —
 * `ModelRegistryRepository.DefaultModelTypeFromScope` takes the LAST `scope_path` segment. Sending a
 * client-side guess would be a second opinion about a value the server derives, and sending the
 * picker's DISPLAY title (`"PD model · IRB"`) rather than the slug would persist a t()-resolved
 * string that differs per UI language — the exact defect WI-35 removed from the workspace-create path.
 */
export interface RegisterModelBody {
  name: string;
  /** Slug path, `"<subOffering>/<...scopeSegments>"`. Create-immutable once persisted (0044). */
  scope_path: string;
  /**
   * REQUIRED — the API refuses `gd_available_required` rather than substituting `false`, because
   * `models.gd_available` is NOT NULL with no default and there is nothing server-side to resolve it
   * from. The one other producer of this value (workspace create) reads it the same way: it is TRUE
   * for a vertical path and FALSE only for the Generic-Task path.
   */
  gd_available: boolean;
  /** Must equal `scope_path`'s depth-2 segment, or the API answers `product_category_scope_mismatch`. */
  product_category?: string;
  applied_to?: string[];
  applied_pairs?: string[];

  // ══ ➕ WI-62 — §3's canonicals, declared on the create request ═══════════════════════════════
  //
  // A MIRROR of `RegisterModelRequest`'s canonical properties, and the JSON names ARE the
  // `model_facts` keys. Every one is optional; a blank one is OMITTED rather than sent empty (see
  // `canonicalRegistrationBody`). The server seeds each into `model_facts` with
  // `source='registration'` / `review_status='confirmed'` inside the model's own transaction.
  //
  // ⚠️ `risk_tier`, `lifecycle_stage` and `built_or_bought` are GOVERNED: a value outside the
  // published vocabulary is refused at the door with `model_fact_value_not_permitted`, which
  // `registerErrorKey()` routes to its own sentence (WI-92).
  description?: string;
  business_unit?: string;
  risk_tier?: string;
  lifecycle_stage?: string;
  built_or_bought?: string;
  regulatory_framework?: string;
}

/**
 * ➕ WI-62 — the canonical half of a register body, with blanks OMITTED.
 *
 * ⛔ **Blank is ABSENT, and the client drops it as well as the server.** `model_facts.fact_value` is
 * `NOT NULL`; a whitespace-only input means the registrar declared nothing, and a persisted `""` is
 * a declaration whose content is nothing — it renders as an empty canonical and there is no fact
 * DELETE to undo it. The server trims and drops blanks too; this half exists so the request itself
 * is honest about what was declared, rather than relying on the far end to clean it up.
 *
 * ⚠️ Takes a plain map so the caller's draft shape is its own business.
 *
 * ⛔ <s>Keys outside `RegisterModelBody` cannot enter: the return type is the picked subset, so a
 * stray draft key is a compile error at the spread site rather than a silently-dropped field on the
 * wire.</s> **THAT WAS FALSE and is struck rather than deleted, because it was load-bearing prose
 * about a safety property that does not exist.** The accumulator is a `Record<string, string>`, so
 * the declared return type constrains nothing at runtime and adding a stray key compiles clean —
 * measured. What actually keeps the key space honest is that the loop iterates
 * `REGISTRATION_FACT_KEYS`, so a key this function could emit is a key the shared list declares.
 *
 * 🔴 **A BLANK KEY IS NOT PRESENT-WITH-`undefined`; IT IS ABSENT.** The first cut of this function
 * returned all six keys with `undefined` for the blanks. `JSON.stringify` drops those, so the WIRE
 * was correct — and `ModelRegisterPage.test.tsx`'s R47 guard failed anyway, because it asserts on
 * the OBJECT (`hasOwnProperty`), not on the serialized bytes. It was right to: a body carrying keys
 * whose emptiness only the serializer removes is a shape that reads as "this field was declared" to
 * every reader between here and the socket, and the guard exists to keep the request honest at the
 * layer a person inspects it.
 */
export function canonicalRegistrationBody(
  draft: Record<string, string>,
): Partial<Pick<RegisterModelBody,
  'description' | 'business_unit' | 'risk_tier' | 'lifecycle_stage' | 'built_or_bought'
  | 'regulatory_framework'>> {
  const out: Record<string, string> = {};
  // ⛔ DERIVED FROM `REGISTRATION_FACT_KEYS`, NOT RE-LISTED. The first cut iterated a literal array
  // of the six names — a FIFTH copy of the key set, and the only one with nothing joining it to the
  // other four. `Wi62RegistrationKeySetTests` joins the C# constant, the client's derivation and the
  // request type; it could not have seen a key that this function alone forgot, and the symptom
  // would have been a field the dialog collects and the request silently drops. That is the exact
  // defect WI-62 exists to close, one layer down from where it was found.
  for (const key of REGISTRATION_FACT_KEYS) {
    const value = draft[key]?.trim();
    if (value) out[key] = value;
  }
  return out;
}

/** `POST /api/models` → 201. The body is the REFETCHED persisted row, per the endpoint's own
 *  persist-then-render note — but see `registerModel` for why a caller still must not render it. */
export interface ModelCreated {
  model: ModelRow;
}

/**
 * Register a model. ADMIN-ONLY server-side (locked decision L2); a non-admin gets `admin_required`
 * 403 and the UI must GREY its control rather than hide it (R8).
 *
 * ⚠️ **THE RETURNED ROW IS AN IDENTIFIER SOURCE, NOT A RENDER SOURCE.** The endpoint does read the
 * row back before answering, so the body is honest — but `frontend-rules` Persist-Then-Render is a
 * rule about the CLIENT's discipline, not about the server's: *"after any mutation, REFETCH from the
 * backend before updating display."* A caller takes `model.model_id` from here and reads the record
 * with `getModel`; that keeps one code path — the read — as the only thing the screen ever draws.
 *
 * Its closed refusal vocabulary, all typed through `ModelApiError.code`: `request_body_required`,
 * `name_required`, `scope_path_required`, `scope_path_invalid`, `gd_available_required`,
 * `tags_on_ck_denied_model`, `product_category_scope_mismatch`, `applied_to_invalid`,
 * `applied_to_vocab_menu_unavailable`, `model_type_unresolvable`, `admin_required`,
 * `principal_oid_unresolved`, and — ➕ **WI-68**, only when a caller SUPPLIES `model_type` —
 * `model_type_not_offered` / `model_type_vocab_menu_unavailable`. This client never supplies it
 * (see the note on `RegisterModelBody` below), so neither is reachable from the product's own
 * registration form; they exist for the admin API surface, which is the door the WI found open.
 */
export async function registerModel(body: RegisterModelBody): Promise<ModelCreated> {
  return postJson<ModelCreated>('', body);
}

// ══ A-1 / RC-6 — THE WRITE SURFACE: the two routes that existed with no client ═══════════════════
//
// 🔴 THIS IS THE INVERSE OF THE USUAL GAP. `PATCH /api/models/{modelId}` and
// `POST /api/models/{modelId}/archive` have been live since WI-05; nothing in `apps/web` called
// either, so a registered model could not be renamed, re-typed or archived from the product. That
// is a ROUTE WITH NO CONTROL — the opposite of the published-but-unwired shape (WI-10a, WI-30) —
// and it read as "an FE gap" three times this cycle without resolving, because a client alone is
// dead code and a control alone is the unwired shape. They land together or not at all.
//
// ♻️ TRANSPORT: REUSED, NOT REBUILT. Both go through the same `modelFetch` + `jsonOrThrow` pair
// `registerModel` established above — one bounded timeout, one credentialed fetch, one typed error
// vocabulary. ⛔ Do NOT add a third transport: `mdrApi.ts` already carries a duplicate that `B-3`
// is scheduled to consolidate into this one, and a third copy makes that consolidation a three-way
// merge instead of a two-way one.

/**
 * `PATCH /api/models/{modelId}` body — a MIRROR of `Bennie.Api.Models.UpdateModelRequest`, read at
 * source 2026-08-23, and deliberately only of what that type declares.
 *
 * ⚠️ **`row_version` IS REQUIRED, AND IT IS NOT OPTIONAL HERE THE WAY IT IS ON THE WIRE TYPE.** The
 * C# property is `long?` because absence has to be *detectable* server-side — it answers a typed
 * `400 row_version_required` rather than defaulting. `backend-rules` mandates optimistic
 * concurrency, so a client that could omit the token is a client that can produce that 400, and
 * there is no caller who legitimately wants to. Typing it as required makes the omission a compile
 * error instead of a round trip.
 *
 * ⚠️ **AND `scope_path` / `product_category` / `applied_to` / `applied_pairs` ARE ABSENT ON
 * PURPOSE.** They are create-immutable in `0044` and `UpdateModelRequest` does not declare them, so
 * naming them here would let a caller populate fields `System.Text.Json` drops in silence — the same
 * trap `RegisterModelBody` above documents for `description` and the mapped id. Locked rule **R67**
 * is the product-side statement of the same fact: *"only two things change after registration."*
 */
export interface UpdateModelBody {
  /** Trimmed. Whitespace-only is treated as ABSENT server-side, not as a clear. */
  name?: string;
  /** Trimmed. Whitespace-only is treated as ABSENT server-side, not as a clear. */
  model_type?: string;
  /** The `row_version` the caller last READ. See the note above on why it is required here. */
  row_version: number;
}

/**
 * What both write routes answer with on success: the REFETCHED persisted row.
 *
 * ⚠️ **AN ACKNOWLEDGEMENT, NOT A RENDER SOURCE** — the same rule `registerModel` states, and it
 * binds harder here because there is no id to take from the body: the ONLY reason a caller reads
 * this shape is to know the write applied. `frontend-rules` Persist-Then-Render is a rule about the
 * CLIENT's discipline, not about the server's honesty — *"after any mutation, REFETCH from the
 * backend before updating display"* — so the caller follows a successful write with `getModel` and
 * draws from that. One code path, the read, is the only thing the screen ever renders.
 */
export interface ModelWritten {
  model: ModelRow;
}

/**
 * Rename and/or re-type a registered model. **Admin OR the model's owner**; an archived model is
 * closed to EVERY principal including an admin.
 *
 * Its closed refusal vocabulary, all typed through `ModelApiError.code` and all localized under
 * `errors:` (verified 2026-08-23):
 * `model_not_found` (404) · `model_archived` (409) · `model_write_forbidden` (403) ·
 * `principal_oid_unresolved` (403) · `request_body_required` (400) · `empty_patch` (400, with
 * `error_args.mutable`) · `row_version_required` (400) · `model_version_conflict`
 * (409, with `error_args.row_version` — the row's ACTUAL current version) ·
 * ➕ **WI-68** `model_type_not_offered` (400, with `error_args.model_type` / `error_args.scope_path`)
 * and `model_type_vocab_menu_unavailable` (503, with `error_args.scope_path`).
 *
 * ⚠️ **`model_type` IS A CLOSED VOCABULARY AS OF WI-68, AND THE RULE IS NOT "it must be in the
 * menu".** It is: *a CHANGE to `model_type` must land on a value the served menu offers under this
 * model's scope.* Restating the value the row already holds is not a change and is not validated —
 * without that, every model carrying a pre-WI-68 free-text value would be permanently un-renameable,
 * because this dialog posts `name` and `model_type` together on every save.
 *
 * 🔴 **`model_version_conflict` IS THE ONE A CALLER MUST HANDLE, NOT JUST DISPLAY.** Locked rule
 * **R19** — *"refuse, re-read, re-present"*: the write is refused rather than merged or silently
 * retried with a bumped token, and the caller then RE-READS so the person decides again against the
 * other person's version. Showing the refusal over the stale row would ask them to decide twice
 * against the same wrong data.
 */
export async function updateModel(modelId: string, body: UpdateModelBody): Promise<ModelWritten> {
  return jsonOrThrow<ModelWritten>(
    await modelFetch(`/${encodeURIComponent(modelId)}`, DEFAULT_TIMEOUT_MS, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  );
}

/**
 * Archive a model — **the ONE status transition (L6), and it is one-way.** There is no unarchive
 * route and deliberately so: the endpoint's own comment records that *"a route that could only ever
 * be refused by the database trigger is a route that exists to fail."*
 *
 * The row version is a POSITIONAL argument rather than a body object because it is the ENTIRE
 * request — `ArchiveModelRequest` declares one property. An object here would only create a field
 * name a caller can misspell into a `400 row_version_required`.
 *
 * Same refusal vocabulary as `updateModel` minus the patch-shape codes: `model_not_found` (404) ·
 * `model_archived` (409) · `model_write_forbidden` (403) · `principal_oid_unresolved` (403) ·
 * `row_version_required` (400) · `model_version_conflict` (409).
 *
 * 🔴 **WHAT THIS DOES NOT DO, STATED HERE BECAUSE A CALLER'S COPY DEPENDS ON IT.** Locked rule
 * **R34**'s second half — *"anything mid-flight in a model REFUSES the archive and tells the user to
 * wait … refuse, name what is running, and say to come back"* — **IS NOT IMPLEMENTED SERVER-SIDE.**
 * The handler was read in full 2026-08-23: it is `AuthorizeMutationAsync` → require `row_version` →
 * `ArchiveAsync` → re-read → return, and nothing in it enumerates running work. Measured with it:
 * a model row's `status` is read at exactly THREE sites in all of `apps/api` — `ExplainUnappliedAsync`,
 * `ModelAccessResolver`, and workspace CREATE — and at ZERO sites in `services/`, so no pipeline
 * consults it either. An archive therefore stops nothing that is already running. Recorded as
 * **`G4-F3`**; it is not fixed here. ⛔ **A caller must therefore not write copy that promises a
 * refusal, a wait, or a cancellation.** A UI describing a guard the server does not have is the
 * mask-over shape `core-principles.md` prohibits, and it is worse than no control at all.
 */
export async function archiveModel(modelId: string, rowVersion: number): Promise<ModelWritten> {
  return jsonOrThrow<ModelWritten>(
    await modelFetch(`/${encodeURIComponent(modelId)}/archive`, DEFAULT_TIMEOUT_MS, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ row_version: rowVersion }),
    }),
  );
}
